import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, MessageSquare } from "lucide-react";

interface SMSSignUpFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  confirmPassword: string;
  setConfirmPassword: (confirmPassword: string) => void;
  displayName: string;
  setDisplayName: (displayName: string) => void;
  loading: boolean;
  onSubmit: (email: string, password: string, confirmPassword: string, displayName: string, e: React.FormEvent) => void;
  validationError: string;
  setValidationError: (error: string) => void;
  phoneNumber?: string;
  inviteRole?: string;
}

const SMSSignUpForm = ({
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  displayName,
  setDisplayName,
  loading,
  onSubmit,
  validationError,
  setValidationError,
  phoneNumber,
  inviteRole = "vendor"
}: SMSSignUpFormProps) => {
  const handleSubmit = (e: React.FormEvent) => {
    // Clear any previous validation errors
    setValidationError("");
    onSubmit(email, password, confirmPassword, displayName, e);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <div className="flex justify-center mb-2">
          <MessageSquare className="h-8 w-8 text-primary" />
        </div>
        <CardTitle className="text-2xl">Complete Your Invitation</CardTitle>
        <CardDescription>
          You've been invited to join as a {inviteRole}
          {phoneNumber && (
            <div className="text-sm text-muted-foreground mt-1">
              Phone: {phoneNumber}
            </div>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="displayName">Full Name</Label>
            <Input
              id="displayName"
              type="text"
              placeholder="Enter your full name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full"
            />
          </div>

          {validationError && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {validationError}
            </div>
          )}

          <div className="bg-muted/50 p-3 rounded-md text-sm text-muted-foreground">
            <strong>SMS Invitation Benefits:</strong>
            <ul className="mt-1 space-y-1 text-xs">
              <li>• No email verification required</li>
              <li>• Instant {inviteRole} access</li>
              <li>• Ready to use immediately</li>
            </ul>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              `Complete ${inviteRole} Setup`
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default SMSSignUpForm;